if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this._onThumbClick = this._onThumbClick.bind(this);
        this._onVariantUpdate = this._onVariantUpdate.bind(this);

        // Cache of REAL DOM nodes by group
        this._groupSlides = new Map();
        this._groupThumbs = new Map();

        // Detached storage roots so nodes stay alive without reparsing
        this._detachedSlidesRoot = document.createDocumentFragment();
        this._detachedThumbsRoot = document.createDocumentFragment();

        this._masterCacheBuilt = false;
        this._lastDomSignature = null;

        this._pendingGroupKey = null;
        this._pendingMediaId = null;

        this._applyTimer = null;
        this._activeGroupKey = null;
        this._switchingClass = "is-variant-switching";

        this._swapInFlight = false;
      }

      connectedCallback() {
        this.removeEventListener("click", this._onThumbClick);
        this.addEventListener("click", this._onThumbClick);

        try {
          if (typeof MainEvents !== "undefined" && MainEvents.variantUpdate) {
            document.removeEventListener(MainEvents.variantUpdate, this._onVariantUpdate);
            document.addEventListener(MainEvents.variantUpdate, this._onVariantUpdate);
          }
        } catch (e) {
          // silent
        }

        this._queueInitialApply();
      }

      disconnectedCallback() {
        this.removeEventListener("click", this._onThumbClick);

        try {
          if (typeof MainEvents !== "undefined" && MainEvents.variantUpdate) {
            document.removeEventListener(MainEvents.variantUpdate, this._onVariantUpdate);
          }
        } catch (e) {
          // silent
        }

        if (this._applyTimer) {
          clearTimeout(this._applyTimer);
          this._applyTimer = null;
        }

        this.classList.remove(this._switchingClass);
        this._swapInFlight = false;
      }

      /* ---------------------------------------------
        Events
      --------------------------------------------- */

      _onThumbClick(e) {
        const btn = e.target.closest(".swiper-controls__thumbnail[data-media-id]");
        if (!btn || !this.contains(btn)) return;

        e.preventDefault();
        e.stopPropagation();

        const mediaId = btn.getAttribute("data-media-id");
        if (mediaId) this.setActiveMedia(mediaId, true);
      }

      _onVariantUpdate(event) {
        this.classList.add(this._switchingClass);

        const info = this._extractVariantInfo(event);
        this._pendingGroupKey = info.groupKey || null;
        this._pendingMediaId = info.mediaId || null;

        this._queueApplyFromVariantEvent();
      }

      /* ---------------------------------------------
        Normalizers
      --------------------------------------------- */

      _normalizeId(id) {
        if (!id) return null;
        const s = String(id);
        const m = s.match(/(\d+)\s*$/);
        return m ? m[1] : s;
      }

      _extractVariantInfo(event) {
        try {
          const v = event && event.detail && event.detail.resource ? event.detail.resource : null;

          const variantId = this._normalizeId(v && v.id ? v.id : null);
          const featuredMediaId = this._normalizeId(
            v && v.featured_media && v.featured_media.id ? v.featured_media.id : null
          );

          const domGroupKey = featuredMediaId ? this._getGroupFromMediaId(featuredMediaId) : null;

          return {
            groupKey: domGroupKey || variantId || null,
            mediaId: featuredMediaId || null,
          };
        } catch (e) {
          return { groupKey: null, mediaId: null };
        }
      }

      /* ---------------------------------------------
        Scheduling
      --------------------------------------------- */

      _queueInitialApply() {
        this._applyTimer = setTimeout(() => {
          this._applyTimer = null;
          this._applyCurrentStateWithRetries();
        }, 0);
      }

      _queueApplyFromVariantEvent() {
        if (this._applyTimer) clearTimeout(this._applyTimer);

        requestAnimationFrame(() => {
          this._applyTimer = setTimeout(() => {
            this._applyTimer = null;
            this._applyCurrentStateWithRetries();
          }, 0);
        });
      }

      _applyCurrentStateWithRetries() {
        let tries = 0;

        const run = () => {
          tries += 1;

          const isCarousel = this._hasCarouselDom();
          const hasGrid = Boolean(this.querySelector(".media-gallery__grid"));

          if (!isCarousel && !hasGrid) {
            this.classList.remove(this._switchingClass);
            return;
          }

          if (isCarousel) {
            const mainWrapper = this._getMainWrapper();
            const thumbsWrapper = this._getThumbsWrapper();

            if (!mainWrapper || !thumbsWrapper) {
              if (tries < 40) return setTimeout(run, 80);
              this.classList.remove(this._switchingClass);
              return;
            }
          }

          this._syncCacheWithDom();
          this._ensureMasterCache();

          let groupKey = this._pendingGroupKey;

          if (!groupKey) {
            const activeMediaId = this._getCurrentActiveMediaId();
            groupKey = this._getGroupFromMediaId(activeMediaId);
          }

          if (!groupKey) {
            const first = this._groupSlides.keys().next();
            groupKey = first && !first.done ? String(first.value) : null;
          }

          if (!groupKey) {
            if (tries < 40) return setTimeout(run, 80);
            this.classList.remove(this._switchingClass);
            return;
          }

          this._filterGrid(groupKey);

          const targetMediaId =
            this._pendingMediaId ||
            this._getFirstMediaIdForGroup(groupKey) ||
            this._getCurrentActiveMediaId();

          this._pendingGroupKey = null;
          this._pendingMediaId = null;

          if (targetMediaId) {
            this.setActiveMedia(String(targetMediaId), false);
          } else {
            this.classList.remove(this._switchingClass);
          }
        };

        run();
      }

      /* ---------------------------------------------
        DOM signature / cache invalidation
      --------------------------------------------- */

      _syncCacheWithDom() {
        const sig = this._getDomSignature();
        if (!sig) return;

        if (sig !== this._lastDomSignature) {
          this._lastDomSignature = sig;
          this._resetMasterCache();
        }
      }

      _getDomSignature() {
        const mainWrapper = this._getMainWrapper();
        const thumbsWrapper = this._getThumbsWrapper();

        const mainSig = mainWrapper
          ? Array.from(mainWrapper.querySelectorAll(".swiper-slide[data-variant-media-group]"))
              .filter((s) => !s.classList.contains("swiper-slide-duplicate"))
              .map((s) => {
                const g = s.getAttribute("data-variant-media-group") || "";
                const mid =
                  s.querySelector("[data-media-id]")?.getAttribute("data-media-id") ||
                  s.getAttribute("data-media-id") ||
                  "";
                return `${g}:${mid}`;
              })
              .join("|")
          : "";

        const thumbsSig = thumbsWrapper
          ? Array.from(
              thumbsWrapper.querySelectorAll(
                ".swiper-controls__thumbnail.swiper-slide[data-variant-media-group][data-media-id]"
              )
            )
              .map((b) => `${b.getAttribute("data-variant-media-group")}:${b.getAttribute("data-media-id")}`)
              .join("|")
          : "";

        if (!mainSig && !thumbsSig) return "";
        return `${mainSig}__${thumbsSig}`;
      }

      _resetMasterCache() {
        this._masterCacheBuilt = false;
        this._groupSlides.clear();
        this._groupThumbs.clear();
        this._detachedSlidesRoot = document.createDocumentFragment();
        this._detachedThumbsRoot = document.createDocumentFragment();
      }

      /* ---------------------------------------------
        Cache building with REAL nodes
      --------------------------------------------- */

      _ensureMasterCache() {
        if (this._masterCacheBuilt) return;

        const mainWrapper = this._getMainWrapper();
        const thumbsWrapper = this._getThumbsWrapper();

        if (!mainWrapper || !thumbsWrapper) {
          this._masterCacheBuilt = true;
          return;
        }

        const baseSlides = Array.from(mainWrapper.children).filter((el) => {
          return (
            el.classList &&
            el.classList.contains("swiper-slide") &&
            !el.classList.contains("swiper-slide-duplicate") &&
            el.hasAttribute("data-variant-media-group")
          );
        });

        for (const slide of baseSlides) {
          const g = slide.getAttribute("data-variant-media-group");
          if (!g) continue;

          const key = String(g);
          if (!this._groupSlides.has(key)) this._groupSlides.set(key, []);
          this._groupSlides.get(key).push(slide);
        }

        const thumbSlides = Array.from(
          thumbsWrapper.querySelectorAll(".swiper-controls__thumbnail.swiper-slide[data-variant-media-group]")
        );

        for (const btn of thumbSlides) {
          const g = btn.getAttribute("data-variant-media-group");
          if (!g) continue;

          const key = String(g);
          if (!this._groupThumbs.has(key)) this._groupThumbs.set(key, []);
          this._groupThumbs.get(key).push(btn);
        }

        this._masterCacheBuilt = true;
      }

      _getFirstMediaIdForGroup(groupKey) {
        const key = String(groupKey);

        const thumbs = this._groupThumbs.get(key) || [];
        for (const thumb of thumbs) {
          const mediaId = thumb.getAttribute("data-media-id");
          if (mediaId) return mediaId;
        }

        const slides = this._groupSlides.get(key) || [];
        for (const slide of slides) {
          const mediaEl = slide.querySelector("[data-media-id]");
          const mediaId =
            (mediaEl && mediaEl.getAttribute("data-media-id")) || slide.getAttribute("data-media-id");
          if (mediaId) return mediaId;
        }

        return null;
      }

      /* ---------------------------------------------
        DOM helpers
      --------------------------------------------- */

      _hasCarouselDom() {
        return Boolean(this._getSwiperComponent()) && Boolean(this._getMainWrapper());
      }

      _getMainWrapper() {
        return this.querySelector("swiper-component .swiper-wrapper") || this.querySelector(".swiper-wrapper");
      }

      _getThumbsWrapper() {
        return this.querySelector(".swiper-controls__thumbnails .swiper-wrapper");
      }

      _getSwiperComponent() {
        return this.querySelector("swiper-component");
      }

      _getMainSwiper() {
        const comp = this._getSwiperComponent();
        return comp && comp.initSwiper ? comp.initSwiper : null;
      }

      _getThumbSwiper() {
        const comp = this._getSwiperComponent();

        if (
          comp &&
          comp.initSwiper &&
          comp.initSwiper.params &&
          comp.initSwiper.params.thumbs &&
          comp.initSwiper.params.thumbs.swiper
        ) {
          return comp.initSwiper.params.thumbs.swiper;
        }

        const thumbSwiperEl = this.querySelector(".swiper-controls__thumbnails-container .swiper");
        return thumbSwiperEl && thumbSwiperEl.swiper ? thumbSwiperEl.swiper : null;
      }

      _getCurrentActiveMediaId() {
        const active =
          this.querySelector('[data-media-id].active') ||
          this.querySelector(".swiper-controls__thumbnail[data-media-id]") ||
          this.querySelector("[data-media-id]");

        return active ? active.getAttribute("data-media-id") : null;
      }

      _getGroupFromMediaId(mediaId) {
        const clean = this._normalizeId(mediaId);
        if (!clean) return null;

        const el =
          this.querySelector(`[data-media-id="${CSS.escape(clean)}"]`) ||
          this.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);

        if (el) {
          const groupEl = el.closest("[data-variant-media-group]");
          const g = groupEl ? groupEl.getAttribute("data-variant-media-group") : null;
          if (g) return String(g);
        }

        for (const [groupKey, slides] of this._groupSlides.entries()) {
          const found = slides.some((slide) => {
            const match =
              slide.querySelector(`[data-media-id="${CSS.escape(clean)}"]`) ||
              slide.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`) ||
              slide.getAttribute("data-media-id") === clean ||
              slide.getAttribute("data-media-id") === String(mediaId);
            return Boolean(match);
          });

          if (found) return String(groupKey);
        }

        for (const [groupKey, thumbs] of this._groupThumbs.entries()) {
          const found = thumbs.some((thumb) => {
            const mid = thumb.getAttribute("data-media-id");
            return mid === clean || mid === String(mediaId);
          });

          if (found) return String(groupKey);
        }

        return null;
      }

      _getMainSlideIndexForMediaId(mediaId) {
        const clean = this._normalizeId(mediaId);
        if (!clean) return -1;

        const mainWrapper = this._getMainWrapper();
        if (!mainWrapper) return -1;

        const slides = Array.from(mainWrapper.children).filter((s) => {
          return s.classList && s.classList.contains("swiper-slide") && !s.classList.contains("swiper-slide-duplicate");
        });

        return slides.findIndex((slide) => {
          return (
            slide.querySelector(`[data-media-id="${CSS.escape(clean)}"]`) ||
            slide.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`) ||
            slide.getAttribute("data-media-id") === clean ||
            slide.getAttribute("data-media-id") === String(mediaId)
          );
        });
      }

      _getRenderedMainSlides() {
        const mainWrapper = this._getMainWrapper();
        if (!mainWrapper) return [];

        return Array.from(mainWrapper.children).filter((el) => {
          return el.classList && el.classList.contains("swiper-slide") && !el.classList.contains("swiper-slide-duplicate");
        });
      }

      /* ---------------------------------------------
        Grid filtering
      --------------------------------------------- */

      _filterGrid(groupKey) {
        const gridItems = this.querySelectorAll(".media-gallery__grid-item[data-variant-media-group]");
        if (!gridItems.length) return;

        const key = String(groupKey);

        for (const item of gridItems) {
          const g = item.getAttribute("data-variant-media-group");
          const isActive = String(g) === key;

          item.style.setProperty("display", isActive ? "" : "none", "important");
          item.toggleAttribute("hidden", !isActive);
          item.setAttribute("aria-hidden", isActive ? "false" : "true");
        }
      }

      /* ---------------------------------------------
        Low-cost group mounting
      --------------------------------------------- */

      _detachAllKnownNodes() {
        for (const slides of this._groupSlides.values()) {
          for (const slide of slides) {
            if (slide && slide.parentNode) this._detachedSlidesRoot.appendChild(slide);
          }
        }

        for (const thumbs of this._groupThumbs.values()) {
          for (const thumb of thumbs) {
            if (thumb && thumb.parentNode) this._detachedThumbsRoot.appendChild(thumb);
          }
        }
      }

      _mountGroupNodes(groupKey) {
        const key = String(groupKey);

        const slides = this._groupSlides.get(key) || [];
        const thumbs = this._groupThumbs.get(key) || [];

        if (!slides.length || !thumbs.length) return false;

        const mainWrapper = this._getMainWrapper();
        const thumbsWrapper = this._getThumbsWrapper();

        if (!mainWrapper || !thumbsWrapper) return false;

        this._detachAllKnownNodes();

        mainWrapper.replaceChildren(...slides);
        thumbsWrapper.replaceChildren(...thumbs);

        return true;
      }

      _refreshSwiperInstances() {
        const main = this._getMainSwiper();
        const thumbs = this._getThumbSwiper();

        try {
          if (thumbs) {
            if (typeof thumbs.updateSize === "function") thumbs.updateSize();
            if (typeof thumbs.updateSlides === "function") thumbs.updateSlides();
            if (typeof thumbs.updateProgress === "function") thumbs.updateProgress();
            if (typeof thumbs.updateSlidesClasses === "function") thumbs.updateSlidesClasses();
            if (typeof thumbs.update === "function") thumbs.update();
          }
        } catch (e) {
          // silent
        }

        try {
          if (main) {
            if (main.params && main.params.loop) {
              if (typeof main.loopDestroy === "function") main.loopDestroy();
              if (typeof main.updateSize === "function") main.updateSize();
              if (typeof main.updateSlides === "function") main.updateSlides();
              if (typeof main.updateProgress === "function") main.updateProgress();
              if (typeof main.updateSlidesClasses === "function") main.updateSlidesClasses();
              if (typeof main.loopCreate === "function") main.loopCreate();
              if (typeof main.update === "function") main.update();
            } else {
              if (typeof main.updateSize === "function") main.updateSize();
              if (typeof main.updateSlides === "function") main.updateSlides();
              if (typeof main.updateProgress === "function") main.updateProgress();
              if (typeof main.updateSlidesClasses === "function") main.updateSlidesClasses();
              if (typeof main.update === "function") main.update();
            }
          }
        } catch (e) {
          // silent
        }
      }

      _moveMainSwiperToMedia(mediaId, smooth) {
        const main = this._getMainSwiper();
        if (!main) return;

        const index = this._getMainSlideIndexForMediaId(mediaId);
        if (index < 0) return;

        const speed = smooth ? 800 : 0;

        try {
          if (main.params && main.params.loop && typeof main.slideToLoop === "function") {
            main.slideToLoop(index, speed);
          } else if (typeof main.slideTo === "function") {
            main.slideTo(index, speed);
          }

          requestAnimationFrame(() => {
            try {
              if (typeof main.updateSize === "function") main.updateSize();
              if (typeof main.updateSlides === "function") main.updateSlides();
              if (typeof main.updateProgress === "function") main.updateProgress();
              if (typeof main.updateSlidesClasses === "function") main.updateSlidesClasses();
              if (typeof main.updateAutoHeight === "function") main.updateAutoHeight(speed ? speed : 0);
              if (typeof main.emit === "function") main.emit("resize");
            } catch (e) {
              // silent
            }
          });
        } catch (e) {
          // silent
        }
      }

      _setActiveClass(mediaId) {
        const clean = this._normalizeId(mediaId);

        this.querySelectorAll('[data-media-id].active').forEach((n) => n.classList.remove("active"));

        const matches = this.querySelectorAll("[data-media-id]");
        matches.forEach((el) => {
          const mid = el.getAttribute("data-media-id");
          if (mid === clean || mid === String(mediaId)) {
            el.classList.add("active");
          }
        });
      }

      _findRenderedMediaElement(mediaId) {
        const clean = this._normalizeId(mediaId);

        return (
          this.querySelector(`swiper-component [data-media-id="${CSS.escape(clean)}"]`) ||
          this.querySelector(`swiper-component [data-media-id="${CSS.escape(mediaId)}"]`) ||
          this.querySelector(`[data-media-id="${CSS.escape(clean)}"]`) ||
          this.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`)
        );
      }

      /* ---------------------------------------------
        Public API
      --------------------------------------------- */

      async setActiveMedia(mediaId, smooth = false) {
        if (!mediaId) return false;

        const cleanMediaId = this._normalizeId(mediaId);
        if (!cleanMediaId) return false;

        const isCarousel = this._hasCarouselDom();
        const groupKey = this._getGroupFromMediaId(cleanMediaId) || this._getGroupFromMediaId(mediaId);

        if (groupKey) this._filterGrid(groupKey);

        if (isCarousel && groupKey) {
          const nextGroupKey = String(groupKey);
          const mustSwitchGroup = this._activeGroupKey !== nextGroupKey;

          if (mustSwitchGroup) {
            if (this._swapInFlight) {
              this._setActiveClass(cleanMediaId);
              return true;
            }

            this._swapInFlight = true;
            this.classList.add(this._switchingClass);

            try {
              this._syncCacheWithDom();
              this._ensureMasterCache();

              const mounted = this._mountGroupNodes(nextGroupKey);
              if (mounted) {
                this._activeGroupKey = nextGroupKey;
                this._refreshSwiperInstances();
              }
            } catch (e) {
              // silent
            } finally {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  this.classList.remove(this._switchingClass);
                  this._swapInFlight = false;
                });
              });
            }
          }
        } else {
          this.classList.remove(this._switchingClass);
        }

        this._setActiveClass(cleanMediaId);

        let element = this._findRenderedMediaElement(cleanMediaId) || this._findRenderedMediaElement(mediaId);

        if (element) {
          let mediaIndex = -1;

          if (isCarousel) {
            mediaIndex = this._getMainSlideIndexForMediaId(cleanMediaId);
            if (mediaIndex < 0) mediaIndex = this._getMainSlideIndexForMediaId(mediaId);
          } else {
            const allMediaElements = Array.from(this.querySelectorAll("[data-media-id]"));
            mediaIndex = allMediaElements.indexOf(element);
          }

          if (!isCarousel) {
            this.syncSlideshow(element, mediaIndex, smooth);
          }

          this.syncThumbnails(cleanMediaId, mediaIndex);

          if (this.classList.contains("media-gallery--grid")) {
            this.swapGridMedia(element, mediaIndex);
          }
        }

        if (isCarousel) {
          setTimeout(() => this._moveMainSwiperToMedia(cleanMediaId, smooth), 0);
        }

        return true;
      }

      syncSlideshow(mediaElement, mediaIndex, smooth = false) {
        try {
          if (!this._hasCarouselDom()) return;

          const swiper = this._getMainSwiper();
          if (!swiper) return;

          const slideElement = mediaElement.closest(".swiper-slide");
          if (slideElement) {
            const slides = this._getRenderedMainSlides();
            const found = slides.indexOf(slideElement);
            if (found >= 0) mediaIndex = found;
          }

          if (mediaIndex >= 0) {
            swiper.slideTo(mediaIndex, smooth ? 800 : 0);
          }
        } catch (e) {
          // silent
        }
      }

      syncThumbnails(mediaId, mediaIndex) {
        try {
          const thumbnailsContainer = this.querySelector(".swiper-controls__thumbnails-container");
          if (!thumbnailsContainer) return;

          const clean = this._normalizeId(mediaId);

          const thumbnailButtons = Array.from(
            thumbnailsContainer.querySelectorAll(".swiper-controls__thumbnail[data-media-id]")
          );

          if (!thumbnailButtons.length) return;

          let target =
            thumbnailButtons.find((b) => {
              const mid = b.getAttribute("data-media-id");
              return mid === clean || mid === String(mediaId);
            }) || null;

          if (!target && mediaIndex >= 0) {
            target = thumbnailButtons[mediaIndex] || null;
          }

          if (!target) return;

          thumbnailButtons.forEach((btn) => btn.classList.remove("active"));
          target.classList.add("active");

          const targetIndex = thumbnailButtons.indexOf(target);
          this.syncThumbnailSwiper(thumbnailsContainer, thumbnailButtons, target, targetIndex);
        } catch (e) {
          // silent
        }
      }

      syncThumbnailSwiper(thumbnailsContainer, thumbnailButtons, targetThumbnail, slideIndex) {
        try {
          const thumbsSwiper = this._getThumbSwiper();
          if (!thumbsSwiper) return;

          if (slideIndex >= 0 && slideIndex < thumbnailButtons.length) {
            thumbsSwiper.slideTo(slideIndex, 300);
          }
        } catch (e) {
          // silent
        }
      }

      swapGridMedia() {
        return;
      }
    }
  );
}